import { useEffect, useRef, useState } from "react"

// Grid resolution of the dissolve. More tiles = finer/smaller pixels (but more
// DOM nodes). These chunky blocks read as a retro "loading in" reveal.
const COLS = 20
const ROWS = 12
const TILES = COLS * ROWS

// A black grid of tiles that covers its parent. The tiles fade out in a random
// stagger ("unloading pixels") to reveal what's behind them.
//
//  - Default: watches its parent <section> and reveals when it scrolls into view
//    (re-covering when it leaves, so the reveal replays on return).
//  - With a `trigger` prop: reveal is controlled by that boolean (e.g. an
//    accordion opening), playing the dissolve on mount / when it flips true.
export default function PixelReveal({ trigger }) {
    const controlled = trigger !== undefined
    const ref = useRef(null)
    const [revealed, setRevealed] = useState(false)

    // Random per-tile delays, generated once so the pattern is stable.
    const delays = useRef(
        Array.from({ length: TILES }, () => Math.random() * 0.6)
    )

    useEffect(() => {
        if (controlled) {
            // Start opaque, then flip on the next frame so the CSS transition
            // actually plays from black -> revealed.
            const raf = requestAnimationFrame(() => setRevealed(trigger))
            return () => cancelAnimationFrame(raf)
        }

        const section = ref.current?.parentElement
        if (!section) return

        const observer = new IntersectionObserver(
            ([entry]) => setRevealed(entry.isIntersecting),
            { threshold: 0.15 }
        )
        observer.observe(section)
        return () => observer.disconnect()
    }, [controlled, trigger])

    return (
        <div
            ref={ref}
            aria-hidden="true"
            className={`pixel-reveal ${revealed ? "is-revealed" : ""}`}
            style={{
                gridTemplateColumns: `repeat(${COLS}, 1fr)`,
                gridTemplateRows: `repeat(${ROWS}, 1fr)`,
            }}
        >
            {delays.current.map((d, i) => (
                <span key={i} style={{ transitionDelay: `${d}s` }} />
            ))}
        </div>
    )
}
